// S-EXEC v2 (B9) — pure formatting for artifact download chips. All the logic
// lives here (100%-tested); the JSX shell just maps over it.

const _KIND = {
  xlsx: "Excel workbook", pptx: "PowerPoint", pdf: "PDF document",
  html: "HTML page", png: "Image", csv: "CSV", docx: "Word document",
  json: "JSON", txt: "Text", file: "File",
};

// Map a filename/MIME to a short badge {label, cls} — cls drives the chip colour.
export function artifactIcon(name = "", mime = "") {
  const ext = String(name).includes(".")
    ? String(name).split(".").pop().toLowerCase() : "";
  const m = String(mime || "").toLowerCase();
  if (ext === "xlsx" || ext === "xls" || m.includes("spreadsheet")) return { label: "XLS", cls: "xlsx" };
  if (ext === "pptx" || ext === "ppt" || m.includes("presentation")) return { label: "PPT", cls: "pptx" };
  if (ext === "pdf" || m.includes("pdf")) return { label: "PDF", cls: "pdf" };
  if (ext === "html" || ext === "htm" || m.includes("html")) return { label: "HTML", cls: "html" };
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "tiff"].includes(ext) || m.startsWith("image/")) return { label: "IMG", cls: "png" };
  if (ext === "csv" || m.includes("csv")) return { label: "CSV", cls: "csv" };
  if (ext === "docx" || ext === "doc" || m.includes("word")) return { label: "DOC", cls: "docx" };
  if (ext === "json" || m.includes("json")) return { label: "{ }", cls: "json" };
  if (ext === "txt" || ext === "md" || m.startsWith("text/")) return { label: "TXT", cls: "txt" };
  return { label: "FILE", cls: "file" };
}

// Human sublabel for the chip ("Excel workbook", "Image", …).
export function artifactKind(name = "", mime = "") {
  return _KIND[artifactIcon(name, mime).cls] || "File";
}

// "39 KB" / "1.2 MB" — never a bare byte count for large files.
export function formatBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return "";
  n = Number(n);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// The chip's sub line: "39 KB · Excel workbook" (or just the kind if size unknown).
export function artifactMeta(a = {}) {
  const size = formatBytes(a.size);
  const kind = artifactKind(a.name, a.mime);
  return size ? `${size} · ${kind}` : kind;
}

// True once a file's download has expired (the API sends expires_at as naive
// UTC; reading it as local time would be off by the viewer's offset). A missing
// or unreadable expiry never hides a file.
export function artifactExpired(a = {}, now = Date.now()) {
  const raw = a && a.expires_at;
  if (!raw) return false;
  const s = String(raw);
  const t = Date.parse(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : `${s}Z`);
  return Number.isFinite(t) && t <= now;
}

// Run-history outcome → a semantic tone ('ok' | 'bad' | 'muted') for the pill.
export function runOutcomeTone(outcome) {
  if (outcome === "completed") return "ok";
  if (["denied", "error", "failed", "timeout", "oom"].includes(outcome)) return "bad";
  return "muted";
}

// A compact meta line for a run row: "2 data reads · 2.3s".
export function runMeta(run = {}) {
  const bc = run.broker_calls ?? 0;
  const reads = `${bc} data read${bc === 1 ? "" : "s"}`;
  const ms = run.duration_ms;
  if (ms == null) return reads;
  const dur = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
  return `${reads} · ${dur}`;
}

// Palette per cls — matches the mockup's chip colours (kept out of JSX for testability).
export function artifactColor(cls) {
  return {
    xlsx: "linear-gradient(135deg,#1D6F42,#25a35b)",
    pptx: "linear-gradient(135deg,#C43E1C,#e0592f)",
    pdf: "linear-gradient(135deg,#B30B00,#e23b2f)",
    png: "linear-gradient(135deg,#0891B2,#A855F7)",
    html: "linear-gradient(135deg,#E44D26,#f2792f)",
  }[cls] || "linear-gradient(135deg,#4F46E5,#A855F7)";
}
